import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGeneral } from '../../contexts';
import Services from '../../services/Services';
import Button from '../../components/ui/Button/Button';
import ImageUploadModal from '../../components/ui/ImageUploadModal/ImageUploadModal';
import FormField from '../../components/ui/Form/FormField';
import formStyles from '../../components/ui/Form/Form.module.css';
import { convertFileToBase64 } from '../../utils/fileConversion';
import { generateCommunityUrl } from '../../utils/urlSlug';
import { CoverFile } from '../../types';
import styles from './CreateChannelPage.module.css';

const CreateChannelPage: React.FC = () => {
  const navigate = useNavigate();
  const { auth, language, toastSuccess, toastError } = useGeneral();
  const { token } = auth;
  const { getTranslation } = language;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  const [logoFile, setLogoFile] = useState<CoverFile | null>(null);
  const [topBannerFile, setTopBannerFile] = useState<CoverFile | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [topBannerPreview, setTopBannerPreview] = useState<string | null>(null);
  const [showLogoModal, setShowLogoModal] = useState(false);
  const [showTopBannerModal, setShowTopBannerModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLogoUpload = async (file: File) => {
    try {
      const coverFile = await convertFileToBase64(file);
      setLogoFile(coverFile);
      const preview = URL.createObjectURL(file);
      setLogoPreview(preview);
    } catch (error) {
      toastError(getTranslation('common.error') || 'Error uploading logo');
    }
  };

  const handleTopBannerUpload = async (file: File) => {
    try {
      const coverFile = await convertFileToBase64(file);
      setTopBannerFile(coverFile);
      const preview = URL.createObjectURL(file);
      setTopBannerPreview(preview);
    } catch (error) {
      toastError(getTranslation('common.error') || 'Error uploading top banner');
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!title.trim()) {
      newErrors.title = getTranslation('createChannel.titleRequired') || 'Title is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    if (!token) {
      toastError(getTranslation('auth.notAuthenticated') || 'You must be logged in to create a channel');
      return;
    }

    setIsSubmitting(true);

    try {
      // Prepare logo and topBanner objects according to API spec
      // The API expects: { file: "base64String", extension: "string", title: "originalFileName" }
      // But based on the codebase, CoverFile only has { file, extension }
      // Let's check if we need to add title field
      const logoPayload = logoFile ? {
        file: logoFile.file,
        extension: logoFile.extension,
        title: 'logo'
      } : null;

      const topBannerPayload = topBannerFile ? {
        file: topBannerFile.file,
        extension: topBannerFile.extension,
        title: 'topBanner'
      } : null;

      const response = await Services.SocialHiveCreateChannel(
        token,
        title.trim(),
        isPublic,
        description.trim() || undefined,
        logoPayload,
        topBannerPayload
      );

      if (response.data.status) {
        toastSuccess(getTranslation('createChannel.success') || 'Channel created successfully');
        // Navigate to the new channel
        const channel = response.data.result;
        if (channel) {
          const channelTitle = channel.channelTitle || channel.title || title.trim();
          const channelID = channel.channelID || channel.id;
          if (channelID) {
            navigate(generateCommunityUrl(channelTitle, channelID));
          }
        } else {
          navigate('/communities');
        }
      } else {
        toastError(response.data.message || getTranslation('createChannel.error') || 'Failed to create channel');
      }
    } catch (error: any) {
      toastError(error.response?.data?.message || getTranslation('createChannel.error') || 'Failed to create channel');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.createChannelPage}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1 className={styles.title}>{getTranslation('createChannel.title') || 'Create Community'}</h1>
          <p className={styles.subtitle}>
            {getTranslation('createChannel.subtitle') || 'Create a new community to organize events and connect with members'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className={formStyles.form}>
          <div className={formStyles.formFields}>
            {/* Image Uploads */}
            <div className={styles.imageUploads}>
              <div className={styles.bannerUpload}>
                <div
                  className={styles.bannerPlaceholder}
                  onClick={() => setShowTopBannerModal(true)}
                >
                  {topBannerPreview ? (
                    <img src={topBannerPreview} alt={getTranslation('createChannel.topBannerLabel') || 'Top Banner'} className={styles.bannerImage} />
                  ) : (
                    <div className={styles.uploadIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.logoUpload}>
                <div
                  className={styles.logoPlaceholder}
                  onClick={() => setShowLogoModal(true)}
                >
                  {logoPreview ? (
                    <img src={logoPreview} alt={getTranslation('createChannel.logoLabel') || 'Logo'} className={styles.logoImage} />
                  ) : (
                    <div className={styles.uploadIcon}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <FormField
              label={getTranslation('createChannel.titleLabel') || 'Community Name'}
              required
              error={errors.title}
            >
              <input
                type="text"
                className={`${formStyles.input} ${errors.title ? formStyles.inputError : ''}`}
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) {
                    setErrors({ ...errors, title: '' });
                  }
                }}
                placeholder={getTranslation('createChannel.titlePlaceholder') || 'Enter community name'}
                disabled={isSubmitting}
              />
            </FormField>

            <FormField
              label={getTranslation('createChannel.descriptionLabel') || 'Description'}
              error={errors.description}
            >
              <textarea
                className={`${formStyles.textarea} ${errors.description ? formStyles.inputError : ''}`}
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  if (errors.description) {
                    setErrors({ ...errors, description: '' });
                  }
                }}
                placeholder={getTranslation('createChannel.descriptionPlaceholder') || 'Enter community description (optional)'}
                rows={4}
                disabled={isSubmitting}
              />
            </FormField>

            <FormField
              label={getTranslation('createChannel.visibilityLabel') || 'Visibility'}
              required
            >
              <div className={formStyles.toggleLabel}>
                <input
                  type="checkbox"
                  className={formStyles.toggle}
                  checked={isPublic}
                  onChange={(e) => setIsPublic(e.target.checked)}
                  disabled={isSubmitting}
                />
                <span className={formStyles.toggleText}>
                  {isPublic 
                    ? (getTranslation('createChannel.public') || 'Public - Anyone can discover and join')
                    : (getTranslation('createChannel.private') || 'Private - Only invited members can join')
                  }
                </span>
              </div>
            </FormField>

          </div>

          <div className={styles.actions}>
            <Button
              type="button"
              variant="secondary"
              onClick={() => navigate('/communities')}
              disabled={isSubmitting}
            >
              {getTranslation('common.cancel') || 'Cancel'}
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={isSubmitting}
            >
              {isSubmitting 
                ? (getTranslation('common.creating') || 'Creating...')
                : (getTranslation('createChannel.createButton') || 'Create Community')
              }
            </Button>
          </div>
        </form>
      </div>

      <ImageUploadModal
        isOpen={showLogoModal}
        onClose={() => setShowLogoModal(false)}
        onUpload={handleLogoUpload}
        title={getTranslation('createChannel.uploadLogo') || 'Upload Logo'}
        aspectRatio="1:1"
        recommendedSize="512x512px"
        allowedTypes={['jpg', 'jpeg', 'png', 'webp']}
        getTranslation={getTranslation}
      />

      <ImageUploadModal
        isOpen={showTopBannerModal}
        onClose={() => setShowTopBannerModal(false)}
        onUpload={handleTopBannerUpload}
        title={getTranslation('createChannel.uploadTopBanner') || 'Upload Top Banner'}
        aspectRatio="3:1"
        recommendedSize="1200x400px"
        allowedTypes={['jpg', 'jpeg', 'png', 'webp']}
        getTranslation={getTranslation}
      />
    </div>
  );
};

export default CreateChannelPage;

